import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ContractsService } from '../../../services/contracts.service';

export interface B2bContractPlateOption {
  plate: string;
  vehicleId?: number | null;
  snapshotId?: number | null;
  model?: string | null;
  operationalStatus?: string | null;
  driverName?: string | null;
  b2bContractDriverId?: number | null;
  label: string;
}

@Component({
  selector: 'app-b2b-contract-plate-select',
  templateUrl: './b2b-contract-plate-select.component.html',
  styleUrls: ['./b2b-contract-plate-select.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => B2bContractPlateSelectComponent),
      multi: true
    }
  ]
})
export class B2bContractPlateSelectComponent implements OnChanges, OnDestroy, ControlValueAccessor {
  @Input() contractId: number | null = null;
  /** Placa vinda de agendamento ou outro prefill — mantida mesmo antes da lista carregar. */
  @Input() prefillOption: B2bContractPlateOption | null = null;
  @Input() label = 'Placa';
  @Input() required = false;
  @Input() allowEmpty = true;
  @Input() emptyLabel = 'Selecione a placa do contrato';
  @Output() plateSelected = new EventEmitter<B2bContractPlateOption | null>();

  options: B2bContractPlateOption[] = [];
  loading = false;
  loadError = false;
  value = '';
  disabled = false;
  /** Filtro rápido por placa/modelo. */
  searchText = '';

  private destroy$ = new Subject<void>();
  private loadCancel$ = new Subject<void>();
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(private contractsService: ContractsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['prefillOption'] && this.prefillOption?.plate) {
      this.applyPrefillValue(this.prefillOption);
    }
    if (changes['contractId']) {
      this.loadPlates();
    }
  }

  ngOnDestroy(): void {
    this.loadCancel$.next();
    this.loadCancel$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: string | null): void {
    const plate = (value || '').toString().trim().toUpperCase();
    if (!plate) {
      this.value = '';
      this.searchText = '';
      return;
    }
    this.applyPrefillValue({ plate, label: plate, vehicleId: this.prefillOption?.vehicleId ?? null });
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onSelectChange(raw: string): void {
    const plate = (raw || '').trim().toUpperCase();
    this.value = plate;
    this.searchText = plate;
    this.onChange(plate || '');
    this.markTouched();

    const option = this.options.find(o => o.plate === plate) || null;
    this.plateSelected.emit(option);
  }

  markTouched(): void {
    this.onTouched();
  }

  onSearchChange(): void {
    const q = this.searchText.trim().toUpperCase();
    if (!q || this.options.some(o => o.plate === q)) {
      return;
    }
    const exact = this.options.find(o => o.plate.startsWith(q));
    if (exact && q.length >= 3) {
      this.onSelectChange(exact.plate);
    }
  }

  clearSearch(): void {
    this.searchText = '';
  }

  get filteredOptions(): B2bContractPlateOption[] {
    const q = this.searchText.trim().toUpperCase();
    if (!q) {
      return this.options;
    }
    return this.options.filter(o =>
      o.plate.includes(q)
      || (o.label || '').toUpperCase().includes(q)
      || (o.model || '').toUpperCase().includes(q)
    );
  }

  get selectedOption(): B2bContractPlateOption | null {
    const plate = (this.value || '').trim().toUpperCase();
    if (!plate) {
      return null;
    }
    return this.options.find(o => o.plate === plate) || null;
  }

  reload(): void {
    this.loadPlates();
  }

  private loadPlates(): void {
    this.loadCancel$.next();
    this.options = [];
    this.loadError = false;

    if (!this.contractId || this.contractId <= 0) {
      this.applyPrefillOptionsOnly();
      return;
    }

    this.loading = true;
    this.contractsService.listB2bContractVehicles(this.contractId, 1, 1000)
      .pipe(takeUntil(this.loadCancel$), takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const rows = res?.data || [];
          const byPlate = new Map<string, B2bContractPlateOption>();

          rows.forEach((row: any) => {
            const plate = String(row.plate || row.Plate || '').trim().toUpperCase();
            if (!plate) return;

            const model = row.model || row.Model || null;
            const status = row.operationalStatus || row.OperationalStatus || null;
            const driverName = row.driverName || row.DriverName || null;
            const option: B2bContractPlateOption = {
              plate,
              vehicleId: row.vehicleId ?? row.VehicleId ?? null,
              snapshotId: row.id ?? row.Id ?? null,
              model,
              operationalStatus: status,
              driverName,
              b2bContractDriverId: row.b2BContractDriverId ?? row.b2bContractDriverId ?? row.B2BContractDriverId ?? null,
              label: [plate, model, driverName ? `Condutor: ${driverName}` : null, status].filter(Boolean).join(' — ')
            };

            if (!byPlate.has(plate)) {
              byPlate.set(plate, option);
            }
          });

          this.mergePrefillIntoOptions(byPlate);
          this.options = Array.from(byPlate.values()).sort((a, b) => a.plate.localeCompare(b.plate));
          this.loading = false;
          this.reconcileValueAfterLoad();
        },
        error: () => {
          this.loading = false;
          this.loadError = true;
          this.applyPrefillOptionsOnly();
        }
      });
  }

  private applyPrefillOptionsOnly(): void {
    const prefill = this.buildPrefillOption();
    this.options = prefill ? [prefill] : [];
    this.reconcileValueAfterLoad();
  }

  private applyPrefillValue(option: B2bContractPlateOption): void {
    const plate = (option.plate || '').trim().toUpperCase();
    if (!plate) {
      return;
    }
    this.value = plate;
    this.searchText = plate;
    this.onChange(plate);
    this.ensureOptionInList(option);
    this.plateSelected.emit(this.options.find(o => o.plate === plate) || option);
  }

  private mergePrefillIntoOptions(byPlate: Map<string, B2bContractPlateOption>): void {
    const prefill = this.buildPrefillOption();
    if (prefill?.plate) {
      byPlate.set(prefill.plate, prefill);
    }
  }

  private buildPrefillOption(): B2bContractPlateOption | null {
    const plate = (this.prefillOption?.plate || this.value || '').trim().toUpperCase();
    if (!plate) {
      return null;
    }
    return {
      plate,
      vehicleId: this.prefillOption?.vehicleId ?? null,
      snapshotId: this.prefillOption?.snapshotId ?? null,
      model: this.prefillOption?.model ?? null,
      operationalStatus: this.prefillOption?.operationalStatus ?? null,
      label: this.prefillOption?.label || plate
    };
  }

  private ensureOptionInList(option: B2bContractPlateOption): void {
    const plate = (option.plate || '').trim().toUpperCase();
    if (!plate || this.options.some(o => o.plate === plate)) {
      return;
    }
    this.options = [...this.options, { ...option, plate, label: option.label || plate }]
      .sort((a, b) => a.plate.localeCompare(b.plate));
  }

  private reconcileValueAfterLoad(): void {
    const plate = (this.value || this.prefillOption?.plate || '').trim().toUpperCase();
    if (!plate) {
      return;
    }

    let option = this.options.find(o => o.plate === plate);
    if (!option) {
      const prefill = this.buildPrefillOption();
      if (prefill) {
        this.ensureOptionInList(prefill);
        option = this.options.find(o => o.plate === plate) || prefill;
      }
    }

    if (!option) {
      return;
    }

    this.value = plate;
    this.searchText = plate;
    this.onChange(plate);
    this.plateSelected.emit(option);
  }
}
